// The proxy's IMAP relay (services/proxy/src/tcp/protocol/imap.rs) authenticates
// to the upstream itself with the secret it holds and fakes the login exchange
// to the sandbox, so the real password / OAuth token never reaches the sandbox.
// These values are dummy credentials the sandbox passes to its IMAP client to
// trigger a login attempt; the proxy ignores them.
export const IMAP_PASSWORD_PLACEHOLDER = "__3B_IMAP_PASSWORD_PLACEHOLDER__";
export const IMAP_ACCESS_TOKEN_PLACEHOLDER =
  "__3B_IMAP_ACCESS_TOKEN_PLACEHOLDER__";

const DEFAULT_PORT = "993";

export function hostComponent(host: string | undefined) {
  const value = (host ?? "").trim();
  if (/^\[[0-9a-fA-F:.]+\]$/.test(value)) return value;
  if (!/^[A-Za-z0-9.-]+$/.test(value)) {
    throw new Error("Invalid IMAP host");
  }
  return value;
}

export function portComponent(port: string | undefined) {
  const value = (port || DEFAULT_PORT).trim();
  if (!/^\d+$/.test(value)) throw new Error("Invalid IMAP port");
  const portNumber = Number(value);
  if (portNumber < 1 || portNumber > 65535) {
    throw new Error("Invalid IMAP port");
  }
  return value;
}

export function tlsComponent(tls: string | undefined) {
  switch (tls) {
    case "implicit":
    case "starttls":
    case "off":
      return tls;
    default:
      return "implicit";
  }
}

// Both connectors test the same way: the sandbox attempts a plain LOGIN with a
// dummy password and the proxy authenticates upstream with the real credential
// (password or OAuth token, per the connector). The TLS mode is encoded the way
// SMTP does it — an `imaps://` scheme for implicit TLS, otherwise an `imap://`
// scheme with a `requireTLS`/`ignoreTLS` flag.
export function connectionString({
  host,
  port,
  username,
  tls,
}: Record<string, string>) {
  const imapTls = tlsComponent(tls);
  const scheme = imapTls === "implicit" ? "imaps" : "imap";
  const params = new URLSearchParams();
  if (imapTls === "starttls") params.set("requireTLS", "true");
  if (imapTls === "off") params.set("ignoreTLS", "true");
  const query = params.size > 0 ? `?${params}` : "";
  const encodedUsername = encodeURIComponent(username ?? "");
  return `${scheme}://${encodedUsername}:${IMAP_PASSWORD_PLACEHOLDER}@${hostComponent(host)}:${portComponent(port)}${query}`;
}
